<template>
  <div class="m-progress-bar">
    <div class="m-progress-bar__container">
      <div class="m-progress-bar__bar-background" />
      <MIntersectionObserver @intersect-enter="handleEnter">
        <div
          class="m-progress-bar__bar"
          :class="[
            { 'm-progress-bar__bar--animate-transform': animateOnEnter },
            { 'm-progress-bar__bar--visible': visible || !animateOnEnter },
            `m-progress-bar__bar--color-${color}`,
          ]"
          :style="{ width: width + '%' }"
        />
      </MIntersectionObserver>
      <span class="sr-only"> {{ width }}% of the way between </span>
    </div>
    <MSpacing v-if="startLabel || endLabel" top="xxs">
      <!-- eslint-disable-next-line prettier/prettier -->
      <div class="is-flex is-justify-content-space-between">
        <span v-if="startLabel" class="m-progress-bar__label">
          {{ startLabel }}
        </span>
        <span v-if="startLabel && endLabel" class="sr-only">and</span>
        <span
          v-if="endLabel"
          class="m-progress-bar__label m-progress-bar__label--end"
        >
          {{ endLabel }}
        </span>
      </div>
    </MSpacing>
  </div>
</template>

<script>
import MIntersectionObserver from '../../layout/IntersectionObserver/MIntersectionObserver.vue'
import MSpacing from '../../layout/Spacing/MSpacing'

/**
 * A progress indicator component to visually display the current
 * status of progress/completion of something
 */
export default {
  name: 'MProgressBar',
  components: {
    MSpacing,
    MIntersectionObserver,
  },
  props: {
    /**
     * An optional start label for the progress bar.
     */
    startLabel: {
      type: String,
      default: '',
    },
    /**
     * An optional end label for the progress bar.
     */
    endLabel: {
      type: String,
      default: '',
    },
    /**
     * The maximum length of the bar.
     */
    length: {
      type: Number,
      default: 5,
    },
    /**
     * The current value of the bar. 100 / length * value equals
     * the percentage progress of the bar.
     */
    value: {
      type: Number,
      required: true,
    },
    /**
     * Arrange in order you wish for them to show, 3 items will show
     * the first up to 33%, the second up to 66% and the 3rd up to
     * 100%.
     */
    colors: {
      type: Array,
      default: () => ['dark'],
      validator: (prop) => {
        return prop.every((color) => {
          const colors = ['skin', 'light', 'dark', 'low', 'medium', 'high']
          return colors.includes(color)
        })
      },
    },
    /**
     * Set to false if you do not wish for the bar to animate from 0% to its set percentage width when it enters the screen and instead would prefer it to already be the correct width
     */
    animateOnEnter: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      visible: false,
    }
  },
  computed: {
    width() {
      if (this.value >= this.length) {
        return 100
      }
      return (this.value / this.length) * 100
    },
    color() {
      const index =
        this.width === 100
          ? this.colors.length - 1
          : Math.floor((this.colors.length / 100) * this.width)
      return this.colors[index]
    },
  },
  methods: {
    handleEnter() {
      this.visible = true
    },
  },
}
</script>

<style lang="scss" scoped>
@use '../../../sass/framework/mixins/font-size';

.m-progress-bar {
  margin-bottom: var(--spacing-8);

  &__container {
    border-radius: 30px;
    height: 8px;
    overflow: hidden;
    position: relative;
    width: 100%;
  }

  &__bar-background {
    background-color: var(--skin-color);
    height: 100%;
    left: 0px;
    opacity: 0.1;
    position: absolute;
    top: 0px;
    width: 100%;
  }

  &__bar {
    border-bottom-left-radius: 30px;
    border-top-left-radius: 30px;
    height: 8px;
    transform: translateX(-100%);

    @media (prefers-reduced-motion: no-preference) {
      transition: width var(--animation-slow);

      &--animate-transform {
        transition:
          transform var(--animation-slow),
          width var(--animation-slow);
      }
    }

    &--visible {
      transform: none;
    }

    &--color-skin {
      background-color: var(--skin-color);
    }

    &--color-dark {
      background-color: var(--skin-background-dark);
    }

    &--color-light {
      background-color: var(--skin-background-light);
    }

    &--color-low {
      background-color: var(--state-error-dark);
    }

    &--color-medium {
      background-color: var(--state-warning-dark);
    }

    &--color-high {
      background-color: var(--state-success-dark);
    }
  }

  &__label {
    @include font-size.font-size(sm);
    color: var(--skin-excerpt-color);

    &--end {
      text-align: right;
    }
  }
}
</style>
